import { useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { useConfiguration } from '../../../context/configurationContext';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { pathByRouteName } from '../../../util/routes';
import { carIdOf, isCarPayload, normalizeReg, useCurrentCar } from '../../../util/savedCar';
import { carSearchQuery, shouldApplyRememberedCar } from './carSearchLocation';

/**
 * A remembered car searches for itself (P25009-482 follow-up).
 *
 * Before the "Din bil" box, typing a plate here was the only way to get a car
 * search, so the results always matched the car on screen. Since BR-4 a car
 * typed on any page is remembered, and /annonser opened with that car in the
 * box but all 276 results under it. This turns the plain search page into the
 * car's search as soon as the car is known — the same search "Visa delar som
 * passar →" makes. "Byt bil" forgets the car, so after it nothing is applied
 * and the page shows everything.
 *
 * Only on the plain search route (/annonser): the make / model landing pages
 * are about their own car. And only when shouldApplyRememberedCar says the
 * buyer has not chosen sizes, a keyword or a seller themselves. history.replace,
 * not push, so Back does not land on the unfiltered page and bounce again.
 *
 * Renders nothing; mounted ONCE by the search page (the "Din bil" box itself
 * renders twice — sidebar and phone sheet).
 */
const RememberedCarSearch = () => {
  const history = useHistory();
  const location = useLocation();
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const { plateInfo, reg } = useCurrentCar();
  const appliedFor = useRef(null);

  const car = isCarPayload(plateInfo) ? plateInfo : null;
  const plate = normalizeReg(reg);
  /* A car chosen by model has no plate; its car-database id names it
   * (P25009-491). */
  const carKey = plate || (car ? carIdOf(car) : null);
  const searchPath = pathByRouteName('SearchPage', routeConfiguration);

  useEffect(() => {
    if (!car || !carKey) return;
    if (location.pathname !== searchPath) return;
    if (!shouldApplyRememberedCar(location.search)) return;
    // Once per car and URL: never loop on a search that stays unfiltered.
    const key = `${carKey}|${location.search}`;
    if (appliedFor.current === key) return;
    appliedFor.current = key;

    const hubHoleOptions =
      (config?.listing?.listingFields || []).find(f => f.key === 'hubHole')?.enumOptions || [];
    history.replace({
      pathname: location.pathname,
      search: carSearchQuery({ search: location.search, data: car, plate, hubHoleOptions }),
    });
  }, [car, carKey, location.pathname, location.search]);

  return null;
};

export default RememberedCarSearch;
